import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, Button, Typography, Stack, IconButton, Collapse, TextField, InputAdornment } from '@mui/material';
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { GroupService } from "../../../services/groups.service";
import { TagsService } from "../../../services/tags-service";
import { Group } from "../../../types/groups.types";
import { Team } from "../../../types/teams.types";
import { Tag } from "../../../types/tags.types";
import { ComponentPermissionGateway } from "../../../components/component-permission-gateway";
import { customerPermissionFlags } from "../../../constants/systemPermissions.constants";
import { useTheme } from '@mui/material';
import { UserIcon } from "@heroicons/react/24/solid";
import { ImportGroupsTeamsModal } from '../../../components/import-groups-teams-modal';
import { useEffectiveSettings, SETTING } from '../../../hooks/use-effective-settings';

// Team Row Component for List View
const TeamRow: React.FC<{ team: Team; groupId: string; isLast?: boolean }> = ({ team, groupId, isLast = false }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const colorButton = theme.palette.primary.main;

    const {
        items: availableTags,
    } = useSelector((state: RootState) => state.tags);

    const tagsMap: Record<string, typeof availableTags[number]> = {};

    availableTags.forEach((t: Tag) => {
        tagsMap[t._id!] = t;
    });

    const handleTeamClick = () => {
        navigate(`/teams/${team.id}/edit`);
    };

    return (
        <Card
            onClick={handleTeamClick}
            sx={{
                mb: isLast ? 0 : 0.5,
                ml: 1,
                mr: 0,
                p: 1.5,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: theme.palette.grey[50],
                borderLeft: `4px solid ${theme.palette.primary.light}`,
                borderRadius: '0 4px 4px 0',
                boxShadow: 'none',
                border: `1px solid ${theme.palette.grey[200]}`,
                '&:hover': {
                    backgroundColor: theme.palette.primary.light,
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                    transform: 'translateX(2px)',
                    transition: 'all 0.2s ease'
                },
            }}
        >
            <Stack direction="row" alignItems="center" spacing={2} flex={1}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ minWidth: 120, fontSize: '0.95rem' }}>
                    {team.name}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ minWidth: 100, fontSize: '0.8rem' }}>
                    {team.location}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    {team.tags?.slice(0, 3).map((tag, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                backgroundColor: tagsMap[tag]?.color,
                                padding: "2px 6px",
                                borderRadius: "4px",
                                fontSize: "9px",
                                color: "white"
                            }}
                        >
                            {tagsMap[tag]?.name}
                        </Box>
                    ))}
                    {team.tags?.length > 3 && (
                        <Typography variant="caption" color="textSecondary">
                            +{team.tags.length - 3} more
                        </Typography>
                    )}
                </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <UserIcon style={{ width: 16, height: 16, color: colorButton }} />
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                        {team.users?.length ?? 0} members
                    </Typography>
                </Stack>
            </Stack>
        </Card>
    );
};

// Team List View Component
export const TeamListView: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const colorButton = theme.palette.primary.main;
    const [showImportModal, setShowImportModal] = useState(false);
    const [toggleRefresh, setToggleRefresh] = useState(false);
    
    // State to track which groups have their teams expanded (default: all collapsed)
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [searchTerm, setSearchTerm] = useState('');

    const {
        items: unsortedGroups,
    } = useSelector((state: RootState) => state.groups);

    const {
        items: availableTags,
    } = useSelector((state: RootState) => state.tags);

    const groups = useMemo(() => {
        const sortedGroups = [...unsortedGroups]
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            .map(group => ({
                ...group,
                teams: [...(group.teams || [])].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            }));

        // If no search term, return all groups
        if (!searchTerm.trim()) {
            return sortedGroups;
        }

        const searchLower = searchTerm.toLowerCase();

        // Filter groups and teams based on search term
        return sortedGroups
            .map(group => {
                // Filter teams that match the search term
                const filteredTeams = group.teams.filter(team => {
                    // Check team name and location
                    const teamNameMatch = team.name?.toLowerCase().includes(searchLower) || false;
                    const teamLocationMatch = team.location?.toLowerCase().includes(searchLower) || false;
                    
                    // Check if any of the team's tags match (if tags exist)
                    const teamTagsMatch = team.tags && team.tags.length > 0 
                        ? team.tags.some(tagId => {
                            // Find the tag in availableTags by ID
                            const tag = availableTags.find(t => t._id === tagId);
                            return tag && tag.name.toLowerCase().includes(searchLower);
                        })
                        : false;
                    
                    return teamNameMatch || teamLocationMatch || teamTagsMatch;
                });

                // Check if group name, location, or tags match search term
                const groupTagsMatch = group.tags && group.tags.length > 0 
                    ? group.tags.some(tagId => {
                        // Find the tag in availableTags by ID
                        const tag = availableTags.find(t => t._id === tagId);
                        return tag && tag.name.toLowerCase().includes(searchLower);
                    })
                    : false;

                const groupMatches = 
                    group.name.toLowerCase().includes(searchLower) ||
                    group.location?.toLowerCase().includes(searchLower) ||
                    groupTagsMatch;

                // Return group if either group matches OR has matching teams
                if (groupMatches || filteredTeams.length > 0) {
                    return {
                        ...group,
                        teams: groupMatches ? group.teams : filteredTeams
                    };
                }
                
                return null;
            })
            .filter((group): group is Group => group !== null);
    }, [unsortedGroups, searchTerm, availableTags]);

    // Groups start collapsed by default - no initialization needed

    // Auto-expand all groups when searching to show search results
    useEffect(() => {
        if (searchTerm && groups.length > 0) {
            const allGroupIds = new Set(groups.map(group => group.id));
            setExpandedGroups(allGroupIds);
        }
    }, [searchTerm, groups]);

    const toggleGroupExpansion = (groupId: string) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupId)) {
                newSet.delete(groupId);
            } else {
                newSet.add(groupId);
            }
            return newSet;
        });
    };

    const isGroupExpanded = (groupId: string) => expandedGroups.has(groupId);

    useEffect(() => {
        GroupService.fetchAll((isValid, msg) => { });
    }, []);

    useEffect(() => {
        TagsService.fetchAll((isValid, msg) => { });
    }, []);

    useEffect(() => {
        if (toggleRefresh) {
            GroupService.fetchAll((isValid, msg) => { });
        }
    }, [toggleRefresh]);

    const { getSetting, isLoading: isCustomerSettingsLoading } = useEffectiveSettings();
    const userUniqueIdentifier = getSetting(SETTING.userUniqueIdentifier, '');

    return (
        <Box sx={{ marginTop: '0.5%' }}>
            {/* Search Bar with Create Group Button */}
            <Box sx={{ mb: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3}>
                    <TextField
                        variant="outlined"
                        placeholder="Search groups and teams..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            flex: 1,
                            maxWidth: '500px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: theme.palette.grey[300],
                                },
                                '&:hover fieldset': {
                                    borderColor: theme.palette.primary.main,
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: theme.palette.primary.main,
                                },
                            },
                        }}
                    />
                    <Stack direction="row" spacing={2} alignItems="center">
                        <ComponentPermissionGateway role={customerPermissionFlags.GROUP_LEADER}>
                            <ComponentPermissionGateway role={customerPermissionFlags.ADMIN}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setShowImportModal(true)}
                                    sx={{
                                        color: colorButton,
                                        borderColor: colorButton,
                                        fontSize: "14px",
                                        padding: "10px 20px",
                                        fontWeight: 'bold',
                                        borderRadius: '8px',
                                        whiteSpace: 'nowrap',
                                        '&:hover': {
                                            borderColor: colorButton,
                                            backgroundColor: colorButton,
                                            color: 'white',
                                        }
                                    }}
                                    startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                                >
                                    Import Groups & Teams
                                </Button>
                            </ComponentPermissionGateway>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    navigate('/groups/create');
                                }}
                                sx={{
                                    backgroundColor: colorButton,
                                    color: 'white',
                                    fontSize: "14px",
                                    padding: "10px 20px",
                                    fontWeight: 'bold',
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                    }
                                }}
                                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                            >
                                Create Group
                            </Button>
                        </ComponentPermissionGateway>
                    </Stack>
                </Stack>
                {searchTerm && (
                    <Typography 
                        variant="body2" 
                        color="textSecondary" 
                        sx={{ mt: 1, ml: 1 }}
                    >
                        Showing {groups.length} result{groups.length !== 1 ? 's' : ''} for "{searchTerm}"
                    </Typography>
                )}
            </Box>

            {groups.length === 0 && searchTerm ? (
                <Box 
                    sx={{ 
                        textAlign: 'center', 
                        py: 8,
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                >
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                        No results found
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Try adjusting your search terms or check the spelling
                    </Typography>
                </Box>
            ) : (
                groups.map((group: Group, index) => (
                <Box
                    key={group.id}
                    sx={{
                        marginBottom: '2%',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                >
                    {/* Group Header - Clean, professional styling */}
                    <Card 
                        onClick={() => toggleGroupExpansion(group.id)}
                        sx={{ 
                            backgroundColor: 'white',
                            color: theme.palette.text.primary,
                            borderRadius: isGroupExpanded(group.id) ? '6px 6px 0 0' : '6px',
                            mb: 0,
                            border: `1px solid ${theme.palette.grey[200]}`,
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: theme.palette.grey[50],
                                transition: 'background-color 0.2s ease'
                            }
                        }}
                    >
                        <Stack
                            direction="column"
                            sx={{ 
                                p: { xs: 1.5, sm: 2 }
                            }}
                        >
                            {/* Top Row - Group Info */}
                            <Stack 
                                direction="row" 
                                alignItems="center" 
                                justifyContent="space-between"
                                sx={{ mb: { xs: 1.5, sm: 2 } }}
                            >
                                <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 2 }}>
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleGroupExpansion(group.id);
                                        }}
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            backgroundColor: theme.palette.grey[100],
                                            width: { xs: 28, sm: 32 },
                                            height: { xs: 28, sm: 32 },
                                            '&:hover': {
                                                backgroundColor: theme.palette.grey[200],
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                    >
                                        {isGroupExpanded(group.id) ? 
                                            <ExpandLessIcon sx={{ fontSize: { xs: 18, sm: 20 } }} /> : 
                                            <ExpandMoreIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                        }
                                    </IconButton>
                                    <Typography 
                                        variant="h6" 
                                        fontWeight="bold" 
                                        color={theme.palette.text.primary}
                                        sx={{ 
                                            fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: { xs: '120px', sm: 'none' }
                                        }}
                                    >
                                        {group.name}
                                    </Typography>
                                    <Box
                                        sx={{
                                            backgroundColor: '#E3F2FD', // Light blue background
                                            color: '#1976D2', // Primary blue text
                                            borderRadius: '6px',
                                            px: { xs: 0.75, sm: 1 },
                                            py: 0.5,
                                            fontSize: { xs: '10px', sm: '12px' },
                                            fontWeight: 'bold',
                                            border: '1px solid #BBDEFB'
                                        }}
                                    >
                                        GROUP
                                    </Box>
                                </Stack>
                                <ComponentPermissionGateway role={customerPermissionFlags.GROUP_LEADER}>
                                    <Button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/groups/${group.id}/edit`);
                                        }}
                                        sx={{
                                            bgcolor: theme.palette.grey[100],
                                            color: theme.palette.text.secondary,
                                            borderRadius: "50%",
                                            width: { xs: 28, sm: 32 },
                                            height: { xs: 28, sm: 32 },
                                            fontSize: "12px",
                                            minWidth: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: 0,
                                            "&:hover": {
                                                bgcolor: theme.palette.grey[200],
                                                color: theme.palette.text.primary,
                                            },
                                        }}
                                    >
                                        <EditIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                    </Button>
                                </ComponentPermissionGateway>
                            </Stack>
                            {/* Bottom Row - Stats and Actions */}
                            <Stack 
                                direction="row"
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                justifyContent="space-between"
                                spacing={{ xs: 1, sm: 1 }}
                                sx={{ 
                                    minWidth: 0,
                                    flexWrap: { xs: 'wrap', sm: 'nowrap' }
                                }}
                            >
                                {/* Stats Section */}
                                <Stack 
                                    direction="row" 
                                    spacing={{ xs: 2, sm: 3, md: 4 }}
                                    alignItems="center"
                                    sx={{ 
                                        flexWrap: 'wrap',
                                        gap: { xs: 1, sm: 0 }
                                    }}
                                >
                                    <Stack alignItems="center" sx={{ minWidth: 'fit-content' }}>
                                        <Typography 
                                            variant="subtitle1" 
                                            fontWeight="bold" 
                                            color={theme.palette.text.primary}
                                            sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                                        >
                                            {group.teams?.length}
                                        </Typography>
                                        <Typography 
                                            variant="caption" 
                                            color={theme.palette.text.secondary}
                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                        >
                                            Teams
                                        </Typography>
                                    </Stack>
                                    <Stack alignItems="center" sx={{ minWidth: 'fit-content' }}>
                                        <Typography 
                                            variant="subtitle1" 
                                            fontWeight="bold" 
                                            color={theme.palette.text.primary}
                                            sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                                        >
                                            {group.teams?.reduce((prev, team) => {
                                                prev += team.users?.length ?? 0;
                                                return prev;
                                            }, 0)}
                                        </Typography>
                                        <Typography 
                                            variant="caption" 
                                            color={theme.palette.text.secondary}
                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                        >
                                            Members
                                        </Typography>
                                    </Stack>
                                    <Stack alignItems="center" sx={{ minWidth: 'fit-content' }}>
                                        <Typography 
                                            variant="body2" 
                                            color={theme.palette.text.secondary}
                                            fontWeight="500"
                                            sx={{ 
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                textAlign: 'center'
                                            }}
                                        >
                                            {group.location}
                                        </Typography>
                                    </Stack>
                                    {/* Group Tags - More Compact */}
                                    {group.tags && group.tags.length > 0 && (
                                        <Stack 
                                            direction="row" 
                                            spacing={0.5} 
                                            sx={{
                                                display: { xs: 'none', md: 'flex' }, // Show only on larger screens
                                                maxWidth: '120px',
                                                minWidth: 0,
                                            }}
                                        >
                                            {group.tags.slice(0, 1).map((tagId, idx) => {
                                                const tag = availableTags.find(t => t._id === tagId);
                                                return tag ? (
                                                    <Box
                                                        key={idx}
                                                        sx={{
                                                            backgroundColor: tag.color || theme.palette.primary.light,
                                                            color: 'white',
                                                            padding: "3px 8px",
                                                            borderRadius: "12px",
                                                            fontSize: "8px",
                                                            fontWeight: 'bold',
                                                            border: `1px solid ${tag.color || theme.palette.primary.main}`,
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            maxWidth: '60px'
                                                        }}
                                                    >
                                                        {tag.name}
                                                    </Box>
                                                ) : null;
                                            })}
                                            {group.tags.length > 1 && (
                                                <Box
                                                    sx={{
                                                        backgroundColor: theme.palette.grey[400],
                                                        color: 'white',
                                                        padding: "3px 8px",
                                                        borderRadius: "12px",
                                                        fontSize: "8px",
                                                        fontWeight: 'bold',
                                                        whiteSpace: 'nowrap',
                                                        border: `1px solid ${theme.palette.grey[500]}`
                                                    }}
                                                >
                                                    +{group.tags.length - 1}
                                                </Box>
                                            )}
                                        </Stack>
                                    )}
                                </Stack>
                                
                                {/* Add Team Button */}
                                <ComponentPermissionGateway role={customerPermissionFlags.TEAM_LEADER}>
                                    <Button
                                        variant="contained"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            GroupService.setGroup(group);
                                            navigate('/teams/create?source=list');
                                        }}
                                        sx={{
                                            backgroundColor: colorButton,
                                            color: 'white',
                                            fontSize: { xs: "10px", sm: "12px" },
                                            padding: { xs: "4px 8px", sm: "6px 12px" },
                                            minWidth: "auto",
                                            fontWeight: 'bold',
                                            whiteSpace: 'nowrap',
                                            alignSelf: 'center',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                            '&:hover': {
                                                backgroundColor: theme.palette.primary.dark,
                                                boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                                            }
                                        }}
                                        startIcon={<AddIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                                    >
                                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                            Add Team
                                        </Box>
                                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                                            Add
                                        </Box>
                                    </Button>
                                </ComponentPermissionGateway>
                            </Stack>
                        </Stack>
                    </Card>

                    {/* Teams Section - Collapsible */}
                    <Collapse in={isGroupExpanded(group.id)} timeout="auto" unmountOnExit>
                        {(group.teams?.length > 0) ? (
                            <Box 
                                sx={{ 
                                    backgroundColor: 'white',
                                    borderRadius: '0 0 6px 6px',
                                    p: 1.5,
                                    border: `1px solid ${theme.palette.grey[200]}`,
                                    borderTop: `1px solid ${theme.palette.grey[300]}`
                                }}
                            >
                                {/* Teams section header */}
                                <Stack 
                                    direction="row" 
                                    alignItems="center" 
                                    spacing={1}
                                    sx={{ mb: 1.5, ml: 1 }}
                                >
                                    <Box
                                        sx={{
                                            backgroundColor: '#E8F5E8', // Light green background
                                            color: '#2E7D32', // Dark green text
                                            borderRadius: '4px',
                                            px: 1,
                                            py: 0.5,
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            border: '1px solid #C8E6C9'
                                        }}
                                    >
                                        TEAMS
                                    </Box>
                                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                                        {group.teams.length} team{group.teams.length !== 1 ? 's' : ''} in this group
                                    </Typography>
                                </Stack>
                                {group.teams.map((team, index) => (
                                    <TeamRow key={team.id} team={team} groupId={group.id} isLast={index === group.teams.length - 1} />
                                ))}
                            </Box>
                        ) : (
                            <Box 
                                sx={{ 
                                    backgroundColor: 'white',
                                    borderRadius: '0 0 6px 6px',
                                    p: 2,
                                    border: `1px solid ${theme.palette.grey[200]}`,
                                    borderTop: `1px solid ${theme.palette.grey[300]}`
                                }}
                            >
                                {/* Teams section header for empty state */}
                                <Stack 
                                    direction="row" 
                                    alignItems="center" 
                                    spacing={1}
                                    sx={{ ml: 1 }}
                                >
                                    <Box
                                        sx={{
                                            backgroundColor: '#E8F5E8', // Light green background
                                            color: '#2E7D32', // Dark green text
                                            borderRadius: '4px',
                                            px: 1,
                                            py: 0.5,
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            border: '1px solid #C8E6C9'
                                        }}
                                    >
                                        TEAMS
                                    </Box>
                                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                                        No teams in this group
                                    </Typography>
                                </Stack>
                                {/* Removed NoData component for cleaner display */}
                            </Box>
                        )}
                    </Collapse>
                </Box>
                ))
            )}

            {showImportModal &&
                <ImportGroupsTeamsModal
                    visible={showImportModal}
                    onClose={() => setShowImportModal(false)}
                    toggleRefresh={toggleRefresh}
                    setToggleRefresh={setToggleRefresh}
                    uniqueIdentifier={userUniqueIdentifier}
                    isCustomerSettingsLoading={isCustomerSettingsLoading}
                />
            }
        </Box>
    );
};
